import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import proveedorService from '../../services/proveedorService';

interface Proveedor {
    nroProveedor: number;
    nombre: string;
    direccion?: string;
    telefono1?: string;
    email?: string;
    cuit?: string;
    tipoIva?: string;
}

interface ProveedorSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (proveedor: Proveedor) => void;
}

const ProveedorSearch: React.FC<ProveedorSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [proveedores, setProveedores] = useState<Proveedor[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verTodos, setVerTodos] = useState<boolean>(false);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (verTodos) setVerTodos(false);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            // Si es numérico puro, buscar directamente por nroProveedor
            if (compact !== '' && /^\d+$/.test(compact)) {
                try {
                    const proveedor = await proveedorService.getProveedor(Number(compact));
                    setProveedores(proveedor ? [proveedor] : []);
                    if (!proveedor) setError('No se encontraron proveedores.');
                } catch (err: any) {
                    setProveedores([]);
                    setError('No se encontraron proveedores.');
                } finally {
                    setLoading(false);
                }
                return;
            }

            const response = await proveedorService.getProveedores({
                searchTerm: trimmed || undefined,
                pageNumber: 1,
                pageSize: 20 // Límite del backend: entre 1 y 20
            });
            setProveedores(response.items || []);
            if (!response.items || response.items.length === 0) {
                setError('No se encontraron proveedores.');
            }
        } catch (err: any) {
            setError(`Error al buscar proveedores: ${err.message || 'Error desconocido'}`);
            setProveedores([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, verTodos]);

    const handleVerTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(true);
            const pageSize = 20; // Límite del backend
            const first = await proveedorService.getProveedores({
                searchTerm: undefined,
                pageNumber: 1,
                pageSize
            });

            let all: Proveedor[] = first.items || [];
            setProveedores(all);
            const totalPages = first.totalPages || 1;

            if (totalPages > 1) {
                const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
                const promises = pages.map((page) =>
                    proveedorService
                        .getProveedores({ searchTerm: undefined, pageNumber: page, pageSize })
                        .then((resp) => {
                            if (resp && Array.isArray(resp.items)) {
                                const items = resp.items as Proveedor[];
                                setProveedores((prev) => prev.concat(items));
                                all = all.concat(items);
                            }
                        })
                        .catch(() => {})
                );
                await Promise.all(promises);
            }
            if (all.length === 0) {
                setError('No se encontraron proveedores.');
            }
        } catch (err: any) {
            setError(`Error al cargar proveedores: ${err.message || 'Error desconocido'}`);
            setProveedores([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (proveedor: Proveedor) => {
        onSelect(proveedor);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setProveedores([]);
        setError(null);
        onClose();
    };

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setProveedores([]);
            setError(null);
            return;
        }

        // Si se está mostrando "Ver Todos", evitamos la búsqueda automática
        // para no sobreescribir la lista completa con solo la primera página.
        if (verTodos) {
            return;
        }

        const handle = setTimeout(() => {
            handleSearch();
        }, 400);

        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch, verTodos]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Proveedor
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Nombre, CUIT o N° de proveedor"
                        inputProps={{ 'aria-label': 'Buscar proveedor' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleSearch}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                            startIcon={<SearchIcon />}
                        >
                            BUSCAR
                        </Button>
                        <Button 
                            variant={verTodos ? 'contained' : 'text'} 
                            onClick={handleVerTodos}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                        >
                            VER TODOS
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {proveedores.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {proveedores.map((proveedor) => (
                                        <Card 
                                            key={proveedor.nroProveedor} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(proveedor)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Nro. Proveedor: {proveedor.nroProveedor}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(proveedor);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {proveedor.nombre}
                                                        </Typography>
                                                    </Box>
                                                    {proveedor.cuit && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                CUIT
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {proveedor.cuit}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Nro. Proveedor</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>CUIT</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {proveedores.map((proveedor) => (
                                            <TableRow 
                                                key={proveedor.nroProveedor} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(proveedor)}
                                            >
                                                <TableCell>{proveedor.nroProveedor}</TableCell>
                                                <TableCell>{proveedor.nombre}</TableCell>
                                                <TableCell>{proveedor.cuit || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(proveedor);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && proveedores.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron proveedores
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProveedorSearch;


